<script setup lang="ts">
import type { TNormalizedAddress } from "@/domains/address/dto/normalized-address.ts";
import type { FormKitFrameworkContext } from "@formkit/core";
/**
 * @deprecated Use non-formkit-specific AddressAutocompleteInput instead
 */
import type { AutoCompleteCompleteEvent, AutoCompleteItemSelectEvent } from "primevue/autocomplete";
import type { PropType } from "vue";

import { computed, ref, watch } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids.ts";
import { ECountryCode } from "@/domains/country/country.types.ts";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit.ts";
import { useAutocompleteAddressQuery } from "@/hooks/queries/address/use-autocomplete-address-query";
import { isDefined } from "@/types/utils.ts";

import AutoComplete from "primevue/autocomplete";

export interface FormKitAddressAutocompleteProps {
  placeholder?: string;
  name?: string;
  country?: ECountryCode;
  disabled?: boolean;
}

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & FormKitAddressAutocompleteProps>,
  },
});

const query = ref("");
const value = useFormkitComputedValue(props.context);
const country = computed(() => props.context?.country ?? ECountryCode.FRANCE);

const search = (event: AutoCompleteCompleteEvent) => {
  query.value = event.query;
};

const { data: addressSuggestions, isLoading } = useAutocompleteAddressQuery(query, country);

watch(country, (_, oldValue) => {
  if (oldValue) {
    value.value = undefined;
  }
});

function toOptionLabel(normalizedAddress: TNormalizedAddress) {
  return [
    normalizedAddress.data.streetNumber,
    normalizedAddress.data.street,
    normalizedAddress.data.postalCode,
    normalizedAddress.data.city,
  ]
    .filter(isDefined)
    .join(" ");
}
</script>

<template>
  <AutoComplete
    v-model="value"
    :data-test-id="DATA_TEST_IDS.components.addressAutocomplete.component"
    :suggestions="addressSuggestions"
    force-selection
    :loading="isLoading"
    :option-label="toOptionLabel"
    :name="props.context?.name"
    :placeholder="props.context?.placeholder"
    :disabled="props.context?.disabled"
    class="w-full"
    input-class="w-full"
    v-bind="$attrs"
    @complete="search"
    @item-select="(e: AutoCompleteItemSelectEvent) => (value = e.value)"
  >
    <template #item="{ item, index }: { item: TNormalizedAddress; index: number }">
      <div :key="index">
        <h3
          class="text-lg font-bold"
          :data-test-id="DATA_TEST_IDS.components.addressAutocomplete.label()"
        >
          {{ item.data.streetNumber }} {{ item.data.street }}
        </h3>
        <span :data-test-id="DATA_TEST_IDS.components.addressAutocomplete.complement()">
          {{ [item.data.postalCode, item.data.city].join(", ") }}
        </span>
      </div>
    </template>
  </AutoComplete>
</template>

<style scoped></style>
